import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";

export interface FeedAlert {
  id: string;
  title: string;
  source: string;
  cat: string;
  data: string[];
  desc: string;
  alertDate: string;
  receivedAt: number;
  matched: boolean;
  locations: string[];
  blocked?: boolean;
  reason?: string;
}

/**
 * Subscribes to the per-tenant socket.io stream at /ws/alerts. The server's
 * AlertsGateway auto-joins us into our membership tenant rooms during the
 * JWT handshake, so we don't need to subscribe explicitly.
 *
 * Maintains an in-memory ring buffer of the last `bufferSize` alerts (default 50).
 */
export function useAlertFeed(opts: { enabled?: boolean; bufferSize?: number } = {}) {
  const enabled = opts.enabled ?? true;
  const bufferSize = opts.bufferSize ?? 50;
  const [alerts, setAlerts] = useState<FeedAlert[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Socket.io-client picks up auth from cookies via withCredentials so we
    // don't need to extract the JWT manually — the boilerplate uses HTTP-only
    // cookies. Vite proxy routes /socket.io and /ws/* to NestJS on :3000.
    const socket: Socket = io("/ws/alerts", {
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 1_000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => {
      setConnected(false);
    });
    socket.on("connect_error", (e: Error) => {
      setError(e.message);
    });
    socket.on("alert", (alert: FeedAlert) => {
      setAlerts((prev) => {
        const next = [alert, ...prev];
        return next.length > bufferSize ? next.slice(0, bufferSize) : next;
      });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, bufferSize]);

  return { alerts, connected, error, ping: () => socketRef.current?.emit("ping") };
}
